import {
  ACCEPTED_FILE_EXTENSIONS,
  DATA_START_YEAR,
  METRIC_ONE_VALUE,
  METRIC_THREE_VALUE,
  MONTH_OPTIONS,
  NOW,
} from "./constants";
import { DefaultMonthValue, DefaultQuarterValue, SelectOption } from "./types";

const MONTH_ABBREV_BY_VALUE = Object.fromEntries(MONTH_OPTIONS.map((opt) => [opt.value, opt.name]));

/** Builds a descending list of year options from `maxYear` down to DATA_START_YEAR. */
export const buildYearOptions = (maxYear: number = NOW.getFullYear()): SelectOption[] => {
  const options: SelectOption[] = [];

  for (let year = maxYear; year >= Number(DATA_START_YEAR); year -= 1) {
    options.push({
      name: String(year),
      value: String(year),
    });
  }

  return options;
};

export const getDefaultQuarter = (referenceDate: Date = NOW): DefaultQuarterValue => {
  const currentMonth = referenceDate.getMonth();
  const currentQuarter = Math.floor(currentMonth / 3) + 1;

  if (currentQuarter === 1) {
    return {
      year: String(referenceDate.getFullYear() - 1),
      quarter: "Q4",
    };
  }

  return {
    year: String(referenceDate.getFullYear()),
    quarter: `Q${currentQuarter - 1}`,
  };
};

/** Returns the year and quarter that contain `referenceDate` (the current quarter, not the previous one). */
export const getCurrentQuarterPeriod = (referenceDate: Date = NOW): DefaultQuarterValue => {
  const year = referenceDate.getFullYear();
  const quarter = Math.floor(referenceDate.getMonth() / 3) + 1;
  return { year: String(year), quarter: `Q${quarter}` };
};

export const getDefaultMonth = (referenceDate: Date = NOW): DefaultMonthValue => {
  const defaultMonthDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - 4, 1);

  return {
    year: String(defaultMonthDate.getFullYear()),
    month: String(defaultMonthDate.getMonth() + 1).padStart(2, "0"),
  };
};

export const isMetricOne = (metric: string) => metric === METRIC_ONE_VALUE;

/** Returns true only for the exact Metric 3 string value. */
export const isMetricThree = (metric: string) => metric === METRIC_THREE_VALUE;

export const isAcceptedFile = (fileName: string) => {
  const lowerCaseFileName = fileName.toLowerCase();

  return ACCEPTED_FILE_EXTENSIONS.some((extension) => lowerCaseFileName.endsWith(extension));
};

export const formatAuditingPeriod = (
  metric: string,
  quarterPeriod: { year: string; quarter: string },
  monthPeriod: { year: string; month: string }
) => {
  if (isMetricOne(metric)) {
    return `${monthPeriod.year}-${MONTH_ABBREV_BY_VALUE[monthPeriod.month]}`;
  }

  return `${quarterPeriod.year}-${quarterPeriod.quarter}`;
};

export const getFileExtension = (fileName: string) => {
  const splitName = fileName.split(".");

  if (splitName.length < 2) {
    return "";
  }

  return splitName[splitName.length - 1].toLowerCase();
};
